/**
 * Registry for sharing Leaflet map instances between independent Vue apps.
 *
 * When multiple Vue apps on the same page need access to a shared Leaflet map
 * (e.g. layer components that overlay a map from a sibling app), this registry
 * provides a clean coordination mechanism via custom DOM events.
 *
 * The element a layer is mounted on and the element of its map share their parent element: a layer
 * acquires the map inside that parent and no other, whichever of the two is mounted first. A page
 * may hold several maps (a map in a dialog above a page with a map in its search form), so nothing
 * is looked up outside the parent.
 */
import L from 'leaflet';

const MAP_READY_EVENT = 'leaflet-map-ready';

interface MapInfo {
    map: L.Map;
    layersControl: L.Control.Layers | null;
}

const registry: Map<HTMLElement, MapInfo> = new Map();

/**
 * Publish a Leaflet map instance and its layers control.
 */
export function publishLeafletMap(container: HTMLElement, map: L.Map, layersControl?: L.Control.Layers): void {
    registry.set(container, {map, layersControl: layersControl ?? null});
    container.dispatchEvent(new CustomEvent(MAP_READY_EVENT, {
        bubbles: true,
        detail: {map, layersControl: layersControl ?? null},
    }));
}

export function unpublishLeafletMap(container: HTMLElement): void {
    registry.delete(container);
}

export interface AcquiredMap {
    map: L.Map;
    layersControl: L.Control.Layers | null;
}

/**
 * Acquire the Leaflet map next to the layer: at once when the map is published already, otherwise
 * as soon as it gets published. Takes the root element of the layer component. Callback receives
 * map and layers control. Returns a cleanup function.
 */
export function acquireLeafletMap(layerElement: HTMLElement, callback: (result: AcquiredMap) => void): () => void {
    const scope = sharedParentOf(layerElement);
    const leafletContainer = scope.querySelector<HTMLElement>('.leaflet-container');

    if (leafletContainer) {
        const existing = registry.get(leafletContainer);

        if (existing) {
            callback(existing);
            return () => {};
        }
    }

    // The published event bubbles from the container of the map, so the parent hears only the
    // maps inside it.
    const handler = (event: Event) => {
        const detail = (event as CustomEvent).detail as AcquiredMap;
        scope.removeEventListener(MAP_READY_EVENT, handler);
        callback(detail);
    };

    scope.addEventListener(MAP_READY_EVENT, handler);

    return () => scope.removeEventListener(MAP_READY_EVENT, handler);
}

// A Vue app renders the root element of the layer inside the element it was mounted on, and the
// element of the map is a sibling of that one: their parent holds the map and the layers.
function sharedParentOf(layerElement: HTMLElement): HTMLElement {
    return layerElement.parentElement?.parentElement ?? document.body;
}
